import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import LegalBanner from "@/components/LegalBanner";
import Footer from "@/components/Footer";
import HeroCalc from "@/components/home/HeroCalc";
import Conversion from "@/components/home/Conversion";
import { HowItWorks, PathToPurchase, DreamGarage, Ownership, Discovery, LoanRefi, FinalCTA, Faq } from "@/components/home/Sections";
import ContactSection from "@/components/home/ContactSection";
import { Seo } from "@/lib/seo";

const HOME_LD = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "DriveClear",
  url: "https://driveclearapp.com",
  applicationCategory: "FinanceApplication",
  operatingSystem: "iOS, Android, Web",
  description: "Know if the car fits before you buy it. Calculate payments, check affordability, and see your DriveClear Fit score.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

export default function Home() {
  const { hash } = useLocation();
  const [fitState, setFitState] = useState({ fit: null, scenario: null });

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans antialiased" data-testid="home-page">
      <Seo
        title="DriveClear — Know if the car fits before you buy it"
        description="Calculate the payment, see the real monthly cost, and check how the vehicle fits your finances with the DriveClear Fit score. Free, no account required."
        path="/"
        jsonLd={HOME_LD}
      />
      <LegalBanner />
      <Header />
      <main>
        {/* Hero — calculator-first */}
        <section id="calculator" data-testid="hero-section" className="py-14 lg:py-24 bg-[#faf9f7] border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div className="lg:pt-8">
                <h1 className="display-title text-4xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 mb-5">
                  Know if the car fits before you buy it.
                </h1>
                <p className="text-lg sm:text-xl text-stone-600 leading-relaxed max-w-xl mb-8">
                  Calculate the payment, see the real monthly cost, and check how the vehicle fits your finances.
                </p>
                <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-500 font-medium">
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span> Same engine as the app</li>
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span> No credit pull, ever</li>
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span> Nothing stored</li>
                </ul>
              </div>
              <HeroCalc onFitComplete={(fit, scenario) => setFitState({ fit, scenario })} />
            </div>
          </div>
        </section>

        <HowItWorks />
        <Conversion fit={fitState.fit} scenario={fitState.scenario} />
        <PathToPurchase />
        <DreamGarage />
        <Ownership />
        <Discovery />
        <LoanRefi />
        <FinalCTA />
        <Faq />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
